/**
 * Générateur — Attendu 195 — O5 « Il écrit une séquence d'instructions
 * (condition « si … alors » et boucle « répéter … fois »). »
 * Niveau : 4ème | Thème #12 Algorithmique (O5, Niveau 2) | Session A.
 * Source prof : O5 (Créé n°23 « pair ou impair » — croisement divisibilité +
 * Créé n°24 « déplacement conditionnel » — la différence si/sinon).
 *
 * ADAPTATIONS scoring : « exécute à la main » → saisie (PAIR/IMPAIR, corrigé
 * unique). La simulation SPATIALE du labyrinthe (Créé 24 a-b) est adaptée en
 * carte CONCEPTUELLE « si/sinon » (Créé 24 c) — le cœur d'O5 (boucle contenant
 * la conditionnelle) est porté par la carte pair/impair, calculable à 100 %.
 *
 * 5 cartes :
 *  1. pairimpair_exec  — le script répond PAIR/IMPAIR pour n (saisie).
 *  2. pairimpair_test  — quel test choisir pour « pair » ? (QCM).
 *  3. pairimpair_mult  — quel test pour « multiple de k » ? (QCM).
 *  4. pairimpair_boucle — boucle + conditionnelle : quels nombres sont dits ? (QCM multiple).
 *  5. sisinon          — si/sinon vs si-puis-instruction : qu'est-ce qui change ? (QCM).
 */

import {
    randInt, randomChoice, makeQcm, makeSaisie,
} from '../../../utils';
import { svgScript, svgScripts, imgElement } from './algo';

const ATTENDU_ID = 195;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_EXEC = 'Pair ou impair : exécuter le script';
const L_TEST = 'Pair ou impair : le bon test';
const L_MULT = 'Multiple de k : le bon test';
const L_BOUCLE = 'Boucle et conditionnelle : quels nombres sont dits ?';
const L_SISINON = 'Si/sinon ou si-puis-instruction ?';

const scriptPairImpair = () => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'sensing', txt: 'demander « Choisis un nombre entier » et attendre' },
    { t: 'ifelse', cond: 'réponse est divisible par 2',
        body: [{ t: 'looks', txt: 'dire « PAIR »' }],
        elseBody: [{ t: 'looks', txt: 'dire « IMPAIR »' }] },
]);

// ═════════ Carte 1 — exécuter (saisie) ═════════

const genPairImpairExec = () => {
    const n = randInt(2, 99);
    const correct = n % 2 === 0 ? 'PAIR' : 'IMPAIR';
    const texte = `<p><b>On tape le nombre ${n} et le lutin exécute ce script. Que répond-il ?</b></p>`
        + '<p>Réponds par PAIR ou IMPAIR.</p>';
    const payload = makeSaisie(correct, texte,
        { ...opts(L_EXEC), elements_additionnels: imgElement(scriptPairImpair()) });
    payload._meta.params = { n };
    return payload;
};

// ═════════ Carte 2 — le bon test (QCM) ═════════

const genPairImpairTest = () => {
    const pairEx = randInt(1, 20) * 2;         // exemple pair
    const imparEx = randInt(1, 20) * 2 - 1;    // exemple impair
    const correct = '(réponse ÷ 2 est un nombre entier)';
    const choix = [
        correct,
        '(réponse × 2 = réponse)',
        '(réponse > 2)',
        // « + 1 pair » détecte les IMPAIRS = complémentaire exact du test correct
        // (fix rapport 4e, 2026-07-28 : « + 2 pair » était ÉQUIVALENT à
        // « ÷ 2 entier » — même parité → deux bonnes réponses dans le QCM)
        '(réponse + 1 est un nombre pair)',
    ];
    const texte = '<p><b>Le script doit dire PAIR quand le nombre est pair. Quel test placer '
        + `dans la condition « si … alors » ?</b></p>`
        + `<p>Par exemple, il doit dire PAIR pour ${pairEx} et IMPAIR pour ${imparEx}.</p>`;
    const payload = makeQcm(choix, correct, texte, opts(L_TEST));
    payload._meta.params = { pairEx, imparEx };
    return payload;
};

// ═════════ Carte 3 — multiple de k (QCM) ═════════

const genPairImpairMult = () => {
    const k = randInt(3, 9);
    const mult = k * randInt(2, 6);
    const correct = `(réponse ÷ ${k} est un nombre entier)`;
    const choix = [
        correct,
        `(réponse × ${k} = réponse)`,
        `(réponse > ${k})`,
        `(réponse − ${k} = 0)`,
    ];
    const texte = '<p><b>On veut maintenant que le script réagisse quand le nombre est un multiple '
        + `de ${k} (par exemple ${mult}). Quel test choisir ?</b></p>`;
    const payload = makeQcm(choix, correct, texte, opts(L_MULT));
    payload._meta.params = { k, mult };
    return payload;
};

// ═════════ Carte 4 — boucle + conditionnelle (QCM multiple) ═════════

const genPairImpairBoucle = () => {
    const depart = randInt(5, 40);
    const m = 5;
    const nombres = Array.from({ length: m }, (_, i) => depart + 1 + i);   // valeurs successives de réponse
    const dits = nombres.filter((v) => v % 2 === 0);
    const script = svgScript([
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'variable', txt: `mettre réponse à ${depart}` },
        { t: 'repeat', n: m, body: [
            { t: 'variable', txt: 'ajouter 1 à réponse' },
            { t: 'if', cond: 'réponse est paire', body: [
                { t: 'looks', txt: 'dire réponse' },
            ] },
        ] },
    ]);
    const choix = nombres.map(String);
    const correct = dits.map(String);
    const texte = `<p><b>La variable « réponse » part de ${depart}. Le lutin exécute ce script. `
        + 'Quels nombres le lutin dit-il ? (Coche tous les bons.)</b></p>';
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_BOUCLE), elements_additionnels: imgElement(script) }, true);
    payload._meta.params = { depart, m, dits };
    return payload;
};

// ═════════ Carte 5 — si/sinon vs si-puis-instruction (QCM) ═════════

const COULEURS_MUR = ['noir', 'rouge', 'bleu'];
const PRENOMS = ['Léna', 'Youri', 'Sacha', 'Nina', 'Timéo', 'Awa', 'Malo', 'Jade'];

const genSiSinon = () => {
    const couleur = randomChoice(COULEURS_MUR);
    const prenom = randomChoice(PRENOMS);
    const k = randInt(8, 15);
    const cond = `couleur touchée = ${couleur}`;
    const scriptSiSinon = {
        titre: 'Script 1 (si/sinon)',
        blocs: [
            { t: 'repeat', n: k, body: [
                { t: 'ifelse', cond,
                    body: [{ t: 'move', txt: 'tourner ↻ de 90 degrés' }],
                    elseBody: [{ t: 'move', txt: 'avancer d\'une case' }] },
            ] },
        ],
    };
    const scriptApres = {
        titre: 'Script 2 (si, puis avancer)',
        blocs: [
            { t: 'repeat', n: k, body: [
                { t: 'if', cond, body: [{ t: 'move', txt: 'tourner ↻ de 90 degrés' }] },
                { t: 'move', txt: 'avancer d\'une case' },
            ] },
        ],
    };
    const correct = 'Dans le script 2, le lutin avance À CHAQUE tour, même juste après avoir tourné : il fait deux actions au même tour.';
    const choix = [
        correct,
        'Les deux scripts font exactement la même chose.',
        'Le script 2 ne fait jamais avancer le lutin.',
        'Le script 2 tourne deux fois par tour au lieu d\'une.',
    ];
    const texte = `<p><b>Le lutin de ${prenom} suit un couloir en ${k} étapes. On compare deux `
        + 'scripts. Dans le script 1, le bloc « avancer » est le SINON de la condition. Dans le '
        + 'script 2, il est placé APRÈS le « si » (hors alternative). Qu\'est-ce qui change dans '
        + 'le déplacement du lutin ?</b></p>';
    const payload = makeQcm(choix, correct, texte,
        { ...opts(L_SISINON), elements_additionnels: imgElement(svgScripts([scriptSiSinon, scriptApres])) });
    payload._meta.params = { couleur, prenom, k };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    pairimpair_exec_texte_saisie: genPairImpairExec,
    pairimpair_test_qcm_unique: genPairImpairTest,
    pairimpair_mult_qcm_unique: genPairImpairMult,
    pairimpair_boucle_qcm_multiple: genPairImpairBoucle,
    sisinon_qcm_unique: genSiSinon,
};

const CARTES = [
    { id: 'pairimpair_exec', label: L_EXEC },
    { id: 'pairimpair_test', label: L_TEST },
    { id: 'pairimpair_mult', label: L_MULT },
    { id: 'pairimpair_boucle', label: L_BOUCLE },
    { id: 'sisinon', label: L_SISINON },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
